import { useState } from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import {
  TextField,
  Button,
  Container,
  Box,
  Card,
  CardContent,
  InputAdornment,
  Typography,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import Email from '@mui/icons-material/Email';
import Lock from '@mui/icons-material/Lock';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { ASSETS } from '../../../const/assets';
import { useNavigate } from 'react-router-dom';
import {
  useSendOTP,
  useVerifyOTP,
  useResetPassword,
} from '../../../hooks/useAuthencation';

const emailValidationSchema = Yup.object().shape({
  email: Yup.string().email('Invalid email').required('Email is required'),
});

const otpValidationSchema = Yup.object().shape({
  otp: Yup.string()
    .matches(/^[0-9]{4}$/, 'OTP must be exactly 4 digits')
    .required('OTP is required'),
});

const passwordValidationSchema = Yup.object().shape({
  password: Yup.string()
    .min(8, 'Password must be at least 8 characters')
    .max(32, 'Password must not exceed 32 characters')
    .matches(/[A-Z]/, 'At least one uppercase letter required')
    .matches(/[a-z]/, 'At least one lowercase letter required')
    .matches(/[0-9]/, 'At least one number required')
    .matches(/[\W_]/, 'At least one special character required')
    .required('Password is required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password')], 'Passwords must match')
    .required('Confirm password is required'),
});

// Thêm interfaces cho form values
interface EmailFormValues {
  email: string;
}

interface OTPFormValues {
  otp: string;
}

interface PasswordFormValues {
  password: string;
  confirmPassword: string;
}

function ForgotPassword() {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [email, setEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [resetPasswordToken, setResetPasswordToken] = useState('');
  const { mutateAsync: sendOTP, isPending: isSendingOTP } = useSendOTP();
  const { mutateAsync: verifyOTP, isPending: isVerifyingOTP } = useVerifyOTP();
  const { mutateAsync: resetPassword, isPending: isResetting } =
    useResetPassword();

  const steps = ['Enter Email', 'Verify OTP', 'Reset Password'];

  // Khởi tạo giá trị mặc định cho các form
  const initialEmailValues: EmailFormValues = {
    email: '',
  };

  const initialOTPValues: OTPFormValues = {
    otp: '',
  };

  const initialPasswordValues: PasswordFormValues = {
    password: '',
    confirmPassword: '',
  };

  const handleEmailSubmit = async (values: { email: string }) => {
    try {
      await sendOTP({ email: values.email });
      setEmail(values.email);
      setActiveStep(1);
    } catch (error) {
      console.error(error);
    }
  };

  const handleOTPSubmit = async (values: { otp: string }) => {
    try {
      const resetPasswordToken = await verifyOTP({
        email,
        otpCode: values.otp,
      });
      setResetPasswordToken(resetPasswordToken);
      setActiveStep(2);
    } catch (error) {
      console.error(error);
    }
  };

  const handlePasswordSubmit = async (values: {
    password: string;
    confirmPassword: string;
  }) => {
    try {
      await resetPassword({
        email,
        resetPasswordToken,
        newPassword: values.password,
      });
      navigate(`/login?email=${email}&password=${values.password}`);
    } catch (error) {
      console.error(error);
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Formik
            initialValues={initialEmailValues}
            validationSchema={emailValidationSchema}
            onSubmit={handleEmailSubmit}
            enableReinitialize={false}
          >
            {({
              handleChange,
              handleBlur,
              errors,
              touched,
              validateForm,
              setTouched,
              values,
            }) => (
              <Form>
                <Field
                  name="email"
                  as={TextField}
                  label="Email"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  autoComplete="off"
                  value={values.email || ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  helperText={touched.email && errors.email}
                  error={touched.email && Boolean(errors.email)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  variant="contained"
                  color="secondary"
                  fullWidth
                  disabled={isSendingOTP}
                  onClick={async () => {
                    const errors = await validateForm();
                    setTouched({ email: true });
                    if (Object.keys(errors).length === 0) {
                      handleEmailSubmit(values);
                    }
                  }}
                  sx={{
                    borderRadius: '10px',
                    backgroundColor: 'black',
                    color: 'white',
                    height: '44px',
                    fontSize: '16px',
                    fontWeight: 'medium',
                    textTransform: 'none',
                    marginTop: '10px',
                  }}
                >
                  {isSendingOTP ? 'Sending...' : 'Send OTP'}
                </Button>
              </Form>
            )}
          </Formik>
        );

      case 1:
        return (
          <Formik
            initialValues={initialOTPValues}
            validationSchema={otpValidationSchema}
            onSubmit={handleOTPSubmit}
            enableReinitialize={false}
          >
            {({
              handleChange,
              handleBlur,
              errors,
              touched,
              validateForm,
              setTouched,
              values,
            }) => (
              <Form>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  We've sent a 4-digit OTP to{' '}
                  <span className="text-red-500">{email}</span>
                </Typography>
                <Field
                  name="otp"
                  as={TextField}
                  label="Enter 4-digit OTP"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  autoComplete="off"
                  value={values.otp || ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  helperText={touched.otp && errors.otp}
                  error={touched.otp && Boolean(errors.otp)}
                  inputProps={{ maxLength: 4 }}
                />
                <Button
                  variant="contained"
                  color="secondary"
                  fullWidth
                  disabled={isVerifyingOTP}
                  onClick={async () => {
                    const errors = await validateForm();
                    setTouched({ otp: true });
                    if (Object.keys(errors).length === 0) {
                      handleOTPSubmit(values);
                    }
                  }}
                  sx={{
                    borderRadius: '10px',
                    backgroundColor: 'black',
                    color: 'white',
                    height: '44px',
                    fontSize: '16px',
                    fontWeight: 'medium',
                    textTransform: 'none',
                    marginTop: '10px',
                  }}
                >
                  {isVerifyingOTP ? 'Verifying...' : 'Verify OTP'}
                </Button>
              </Form>
            )}
          </Formik>
        );

      case 2:
        return (
          <Formik
            initialValues={initialPasswordValues}
            validationSchema={passwordValidationSchema}
            onSubmit={handlePasswordSubmit}
            enableReinitialize={false}
          >
            {({
              handleChange,
              handleBlur,
              errors,
              touched,
              validateForm,
              setTouched,
              values,
            }) => (
              <Form autoComplete="off">
                <Field
                  name="password"
                  as={TextField}
                  label="New Password"
                  type={showPassword ? 'text' : 'password'}
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  autoComplete="new-password"
                  value={values.password || ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  helperText={touched.password && errors.password}
                  error={touched.password && Boolean(errors.password)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <div
                          onClick={() => setShowPassword(!showPassword)}
                          className="cursor-pointer"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </div>
                      </InputAdornment>
                    ),
                  }}
                />
                <Field
                  name="confirmPassword"
                  as={TextField}
                  label="Confirm New Password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  autoComplete="new-password"
                  value={values.confirmPassword || ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  helperText={touched.confirmPassword && errors.confirmPassword}
                  error={
                    touched.confirmPassword && Boolean(errors.confirmPassword)
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <div
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                          className="cursor-pointer"
                        >
                          {showConfirmPassword ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </div>
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  variant="contained"
                  color="secondary"
                  fullWidth
                  disabled={isResetting}
                  onClick={async () => {
                    const errors = await validateForm();
                    setTouched({ password: true, confirmPassword: true });
                    if (Object.keys(errors).length === 0) {
                      handlePasswordSubmit(values);
                    }
                  }}
                  sx={{
                    borderRadius: '10px',
                    backgroundColor: 'black',
                    color: 'white',
                    height: '44px',
                    fontSize: '16px',
                    fontWeight: 'medium',
                    textTransform: 'none',
                    marginTop: '10px',
                  }}
                >
                  {isResetting ? 'Resetting...' : 'Reset Password'}
                </Button>
              </Form>
            )}
          </Formik>
        );

      default:
        return null;
    }
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      height="100vh"
      position="relative"
      sx={{
        background: '#272727',
      }}
    >
      <img
        className="absolute top-0 left-0 w-full"
        src={ASSETS.bgLogin}
        alt="bgLogin"
      />
      <Container maxWidth="xs">
        <Card
          sx={{
            padding: '20px',
            borderRadius: '16px',
            boxShadow: 3,
            position: 'relative',
            zIndex: 10,
          }}
        >
          <CardContent>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <div
                className="w-10 h-10 flex justify-center items-center border-[#D8DADC] rounded-[10px] border cursor-pointer"
                onClick={() => navigate(-1)}
              >
                <img
                  src={ASSETS.icBack2}
                  alt="back"
                  width={9}
                  height={15}
                  className="cursor-pointer"
                />
              </div>
              <img src={ASSETS.icLogo} alt="logo" width={50} height={50} />
            </Box>

            <Box>
              <Typography
                variant="h6"
                align="left"
                sx={{
                  fontSize: '30px',
                  fontWeight: 'semibold',
                  my: '20px',
                  color: 'black',
                }}
              >
                Reset Password
              </Typography>
            </Box>

            <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {renderStepContent(activeStep)}

            <Button
              variant="text"
              fullWidth
              onClick={() => navigate('/login')}
              sx={{
                color: 'black',
                fontSize: '14px',
                textTransform: 'none',
                fontWeight: 'regular',
                padding: '0',
                marginTop: '10px',
              }}
            >
              Back to Login
            </Button>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}

export default ForgotPassword;
